import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import adminQueueService from '../../../../../services/adminQueueService';
import ServiceTypeModal from './ServiceTypeModal';

function ServiceTypeList() {
	const { t } = useTranslation();
	const [serviceTypes, setServiceTypes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [totalElements, setTotalElements] = useState(0);
	const rowsPerPage = 20;

	const [modalOpen, setModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState(null);

	const [deleteId, setDeleteId] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const fetchServiceTypes = async (p = page) => {
		setLoading(true);
		try {
			const res = await adminQueueService.getAllServiceTypes(p, rowsPerPage);
			setServiceTypes(res.data.content || []);
			setTotalElements(res.data.page?.totalElements ?? res.data.totalElements ?? 0);
		} catch {
			toast.error(t('admin.serviceTypes.loadError'));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchServiceTypes(page);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	const handleAdd = () => {
		setEditingItem(null);
		setModalOpen(true);
	};

	const handleEdit = (item) => {
		setEditingItem(item);
		setModalOpen(true);
	};

	const handleModalSuccess = () => {
		setModalOpen(false);
		fetchServiceTypes(page);
	};

	const handleDeleteConfirm = async () => {
		setDeleting(true);
		try {
			await adminQueueService.deleteServiceType(deleteId);
			toast.success(t('admin.serviceTypes.deletedSuccess'));
			setDeleteId(null);
			const newPage =
				serviceTypes.length === 1 && page > 0 ? page - 1 : page;
			setPage(newPage);
			fetchServiceTypes(newPage);
		} catch (e) {
			toast.error(
				e?.response?.data?.message || t('admin.serviceTypes.deletedError')
			);
		} finally {
			setDeleting(false);
		}
	};

	return (
		<Box>
			<Stack
				direction='row'
				justifyContent='space-between'
				alignItems='center'
				sx={{ mb: 2.5 }}
			>
				<Typography variant='h6' sx={{ fontWeight: 700 }}>
					{t('admin.serviceTypes.title')}{totalElements > 0 && ` (${totalElements})`}
				</Typography>
				<Button
					variant='contained'
					startIcon={<AddRoundedIcon />}
					onClick={handleAdd}
					sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
				>
					{t('admin.serviceTypes.addBtn')}
				</Button>
			</Stack>

			<Paper
				elevation={0}
				sx={{
					borderRadius: 3,
					border: '1px solid rgba(0,0,0,0.08)',
					overflow: 'hidden'
				}}
			>
				<TableContainer>
					<Table size='medium'>
						<TableHead>
							<TableRow sx={{ backgroundColor: 'rgba(25,118,210,0.06)' }}>
								<TableCell sx={{ fontWeight: 700, width: 60 }}>№</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.serviceTypes.colNameKk')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.serviceTypes.colNameRu')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.serviceTypes.colNameEn')}</TableCell>
								<TableCell sx={{ fontWeight: 700, width: 100 }}>{t('admin.serviceTypes.colColor')}</TableCell>
								<TableCell sx={{ fontWeight: 700, width: 100 }}>{t('admin.serviceTypes.colStatus')}</TableCell>
								<TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>{t('admin.serviceTypes.colActions')}</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={7} align='center' sx={{ py: 5 }}>
										<CircularProgress size={32} />
									</TableCell>
								</TableRow>
							) : serviceTypes.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={7}
										align='center'
										sx={{ py: 5, color: 'text.secondary' }}
									>
										{t('admin.serviceTypes.empty')}
									</TableCell>
								</TableRow>
							) : (
								serviceTypes.map((st, idx) => (
									<TableRow
										key={st.id}
										hover
										sx={{ '&:last-child td': { borderBottom: 0 } }}
									>
										<TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
											{page * rowsPerPage + idx + 1}
										</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>{st.nameKk}</TableCell>
										<TableCell>{st.nameRu}</TableCell>
										<TableCell sx={{ color: 'text.secondary' }}>{st.nameEn}</TableCell>
										<TableCell>
											{st.color && (
												<Box
													sx={{
														width: 28,
														height: 28,
														borderRadius: 1,
														backgroundColor: st.color,
														border: '1px solid rgba(0,0,0,0.12)'
													}}
												/>
											)}
										</TableCell>
										<TableCell>
											<Chip
												label={st.isActive ? t('admin.serviceTypes.active') : t('admin.serviceTypes.inactive')}
												size='small'
												color={st.isActive ? 'success' : 'default'}
												variant='outlined'
												sx={{ fontWeight: 600 }}
											/>
										</TableCell>
										<TableCell align='right'>
											<Stack direction='row' justifyContent='flex-end' spacing={0.5}>
												<Tooltip title={t('admin.serviceTypes.tooltipEdit')}>
													<IconButton size='small' color='primary' onClick={() => handleEdit(st)}>
														<EditRoundedIcon fontSize='small' />
													</IconButton>
												</Tooltip>
												<Tooltip title={t('admin.serviceTypes.tooltipDelete')}>
													<IconButton size='small' color='error' onClick={() => setDeleteId(st.id)}>
														<DeleteRoundedIcon fontSize='small' />
													</IconButton>
												</Tooltip>
											</Stack>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					component='div'
					count={totalElements}
					page={page}
					onPageChange={(_, p) => setPage(p)}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[20]}
					labelDisplayedRows={({ from, to, count }) =>
						t('common.from_to_count', { from, to, count })
					}
				/>
			</Paper>

			<ServiceTypeModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				onSuccess={handleModalSuccess}
				editingItem={editingItem}
			/>

			<Dialog
				open={!!deleteId}
				onClose={() => !deleting && setDeleteId(null)}
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle sx={{ fontWeight: 700 }}>{t('admin.serviceTypes.deleteTitle')}</DialogTitle>
				<DialogContent>
					<Typography variant='body2' color='text.secondary'>
						{t('admin.serviceTypes.deleteBody')}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
					<Button
						onClick={() => setDeleteId(null)}
						disabled={deleting}
						sx={{ textTransform: 'none', borderRadius: 2 }}
					>
						{t('common.cancel')}
					</Button>
					<Button
						variant='contained'
						color='error'
						onClick={handleDeleteConfirm}
						disabled={deleting}
						sx={{ textTransform: 'none', borderRadius: 2, minWidth: 100 }}
					>
						{deleting ? (
							<CircularProgress size={18} sx={{ color: 'inherit' }} />
						) : (
							t('common.delete')
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default ServiceTypeList;
